import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { useGetInitMyaccount } from '../hooks/useMyAccount';

import { Button, Divider } from '@/components';
import usePinsAccount from '@/hooks/usePinsAccount';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import {
  initMyAccount,
  purchaseWithAvailableFunds,
} from '@/services/myAccount';
import { TipoProductoTO } from '@/types/myAccount';
import { formatNumberToLocale, PACKS_BY_ID, PACKS_BY_NAME } from '@/utils';

interface Props {
  packs: number[];
  selectedProduct: TipoProductoTO;
  pinsAvailable: number;
  accountMoney: number;
  mounted: boolean;
}

interface ToastMessageProps {
  pin?: string;
  newBalance: number;
  quantity?: number;
  selectedProduct?: TipoProductoTO;
  tourneyName?: string;
  isTDAActive?: boolean;
}

const onlyTDAPacks = [
  PACKS_BY_ID[PACKS_BY_NAME.PIN_ESPECIAL_TDA_1],
  PACKS_BY_ID[PACKS_BY_NAME.PIN_ESPECIAL_TDA_2],
  PACKS_BY_ID[PACKS_BY_NAME.PIN_ESPECIAL_TDA_3],
];
const ToastMessage = ({
  pin,
  newBalance,
  quantity,
  selectedProduct,
  tourneyName,
  isTDAActive,
}: ToastMessageProps) => {
  if (quantity === 1) {
    if (
      selectedProduct &&
      onlyTDAPacks.includes(selectedProduct.id) &&
      tourneyName &&
      !isTDAActive
    ) {
      return (
        <p className="text-center">
          Este es el pin que adquiriste{' '}
          <strong className="text-gdt-status-green-icon">{pin}</strong> <br /> y
          por el que ya estas participando en <strong>{tourneyName}</strong>
        </p>
      );
    }
    if (
      selectedProduct &&
      onlyTDAPacks.includes(selectedProduct.id) &&
      tourneyName &&
      isTDAActive
    ) {
      return (
        <p className="text-center">
          Este es el pin que adquiriste{' '}
          <strong className="text-gdt-status-green-icon">{pin}</strong> <br /> y
          por el que ya estas participando en <strong>{tourneyName}</strong>
        </p>
      );
    }
    if (
      selectedProduct &&
      selectedProduct.id === PACKS_BY_ID[PACKS_BY_NAME.PIN_FECHA_ORO]
    ) {
      return (
        <p className="text-center">
          Tu PIN es{' '}
          <strong className="text-gdt-status-green-icon">{pin}</strong> <br />
          Este PIN quedará vigente en tu cuenta hasta que sea utilizado. Tu
          saldo es de ${newBalance}.
        </p>
      );
    }
    return (
      <div className="flex flex-col items-center">
        <p>
          Tu PIN es{' '}
          <strong className="text-gdt-status-green-icon">{pin}</strong>
        </p>
        <p>
          Este PIN quedará vigente en tu cuenta hasta que sea utilizado. Tu
          saldo es de ${newBalance}.{' '}
          <strong>¡Ahorrá tiempo y dinero comprando paquetes de PINES!</strong>
        </p>
      </div>
    );
  }
  return (
    <p>
      <strong>Compraste un Pack de {quantity} PINES.</strong> Tu saldo es $
      {formatNumberToLocale(newBalance)}.
    </p>
  );
};

export const PayWithBalance = ({
  packs,
  selectedProduct,
  pinsAvailable,
  accountMoney,
  mounted,
}: Props) => {
  const { updatePinsData } = usePinsAccount();
  const { data: myAccount } = useGetInitMyaccount();
  const toast = useToast();
  const queryClient = useQueryClient();
  const router = useNavigate();

  const Content = useCallback(() => {
    if (!selectedProduct) {
      return null;
    }
    const packsFormated = packs
      .filter((p) => p > 1)
      .sort((a, b) => a - b)
      .join(', ')
      .replace(/, ([^,]*)$/, ' y $1');

    if (onlyTDAPacks.includes(selectedProduct?.id)) {
      return (
        <p>
          <strong>
            Acabás de solicitar un Pin Especial $
            {formatNumberToLocale(selectedProduct.precio)}.{' '}
          </strong>{' '}
          Al confirmar el pedido verás el Pin Especial y se te descontarán $
          {formatNumberToLocale(selectedProduct.precio)} de tu saldo. La
          inscripción será automática. ¿Confirmás la transacción?
        </p>
      );
    }

    if (selectedProduct?.id === PACKS_BY_ID[PACKS_BY_NAME.PIN_FECHA_ORO]) {
      return (
        <p>
          <strong>
            Acabás de solicitar un Pin Especial $
            {formatNumberToLocale(selectedProduct.precio)}.
          </strong>{' '}
          Al confirmar el pedido verás el Pin Especial y se te descontarán $
          {formatNumberToLocale(selectedProduct.precio)} de tu saldo. El Pin
          permanecerá almacenado en tu cuenta.
        </p>
      );
    }
    if (selectedProduct?.cantidad === 1 && !selectedProduct.esPremium) {
      return (
        <p>
          Podés adquirir Packs por {packsFormated} pines. Hacé que tu saldo
          rinda. <br /> <strong>Acabás de solicitar un PIN.</strong> Al
          confirmar el pedido verás el PIN y se te descontarán $
          {formatNumberToLocale(selectedProduct.precio)} de tu saldo. El PIN
          permanecerá almacenado en tu cuenta.
        </p>
      );
    }
    if (selectedProduct?.cantidad === 1 && selectedProduct.esPremium) {
      return (
        <p>
          <strong>Acabás de elegir el Pack Premium.</strong> Al confirmar el
          pedido se activará el Pack y se te descontarán $
          {formatNumberToLocale(selectedProduct.precio)} de tu saldo. Con el
          Pack Premium accedés a pines ilimitados para mejorar tus equipos de
          Primera y de Fútbol 5, participás de competencias exclusivas y podés
          crear todos los Torneos de Amigos VIP que desees. Además, si ganás en
          Primera, multiplicás tu premio.
        </p>
      );
    }
    return (
      <p>
        <strong>
          Acabás de solicitar un pack de {selectedProduct?.cantidad} PINES.
        </strong>{' '}
        Al confirmar tu pedido verás los pines y se te descontarán $
        {formatNumberToLocale(selectedProduct?.precio)} de tu saldo. Los pines
        permanecerán almacenados en tu cuenta
      </p>
    );
  }, [packs, selectedProduct]);

  const handleConfirm = useCallback(async () => {
    if (selectedProduct) {
      let newBalance = accountMoney - selectedProduct.precio;
      const purchase = await purchaseWithAvailableFunds(selectedProduct);
      const newBan = await initMyAccount();
      if ('saldo' in newBan && newBan.saldo) {
        newBalance = newBan.saldo;
      }

      if (
        'pines_comprados' in purchase.data &&
        purchase.data.pines_comprados &&
        purchase.data.pines_comprados?.length !== 0
      ) {
        toast.show(
          <ToastMessage
            isTDAActive={
              myAccount && 'tapHabilitado' in myAccount
                ? myAccount.tapHabilitado
                : false
            }
            newBalance={newBalance}
            pin={purchase.data.pines_comprados[0]}
            quantity={selectedProduct.cantidad}
            selectedProduct={selectedProduct}
            tourneyName={purchase.data.resultadoUsoCD?.nombre}
          />
        );
        updatePinsData({
          accountMoney: newBalance,
          selectedProduct: undefined,
          showModalPayWithBalance: false,
          pinsAvailable: pinsAvailable + purchase.data.pines_comprados.length,
        });
      } else if (selectedProduct.esPremium) {
        updatePinsData({
          accountMoney: newBalance,
          selectedProduct: undefined,
          showModalPayWithBalance: false,
          isPremium: true,
        });
      } else {
        toast.show(purchase.data.mensaje);
      }
      queryClient.invalidateQueries('init-my-account');
      if (onlyTDAPacks.includes(selectedProduct.id)) {
        router(ROUTES.friendsTournamentPrizes);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountMoney, myAccount, pinsAvailable, selectedProduct, toast]);

  return (
    <>
      {mounted && (
        <div>
          <Divider className="border-2 mb-7 mt-4" type="horizontal" />
          <Content />

          <div className="flex justify-center mt-6">
            <Button type="button" variant="primary" onClick={handleConfirm}>
              Aceptar
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
